<template>
  <v-card flat class="whats-new section-card mb-4">
    <div class="whats-new__header">
      <div class="whats-new__header-left">
        <v-icon size="20" color="#ff1463" class="mr-2">mdi-star-four-points</v-icon>
        <span class="whats-new__title">What's New</span>
      </div>
      <span class="whats-new__badge">NEW</span>
    </div>

    <div class="whats-new__body">
      <!-- Tracey agents -->
      <div class="whats-new__intro">
        Meet Tracey, your AI procurement analyst. Two agents, built for different jobs.
      </div>

      <div class="whats-new__agents">
        <div
          v-for="agent in agents"
          :key="agent.key"
          class="whats-new__agent"
          :style="{ '--accent': agent.accent }"
        >
          <div class="whats-new__agent-head">
            <v-icon size="18" :color="agent.accent" class="mr-2">mdi-creation</v-icon>
            <span class="whats-new__agent-name">{{ agent.name }}</span>
            <span class="whats-new__agent-tag">{{ agent.tagline }}</span>
          </div>
          <div class="whats-new__caps">
            <span
              v-for="cap in topCapabilities(agent)"
              :key="cap"
              class="whats-new__cap"
            >
              {{ cap }}
            </span>
          </div>
        </div>
      </div>

      <button type="button" class="whats-new__cta" @click="openTracey">
        Try Tracey
        <v-icon size="16" color="#fff" class="ml-1">mdi-arrow-right</v-icon>
      </button>

      <template v-if="reportsMarketplaceVisible">
        <!-- Reports Marketplace -->
        <div class="whats-new__divider" />

        <a
          :href="marketplaceUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="whats-new__market"
        >
          <div class="whats-new__market-icon">
            <v-icon size="22" color="#12253a">mdi-storefront-outline</v-icon>
          </div>
          <div class="whats-new__market-text">
            <div class="whats-new__market-title">
              Reports Marketplace
              <v-icon size="14" color="#98a2b3" class="ml-1">mdi-open-in-new</v-icon>
            </div>
            <p class="whats-new__market-desc">
              Browse ready-made procurement intelligence reports across agencies, categories and markets. Explore, download and put insights to work in minutes.
            </p>
          </div>
        </a>
      </template>
    </div>
  </v-card>
</template>

<script>
import { TRACEY_AGENTS } from '../tracey/agentVersions.js';

export default {
  name: 'WhatsNewPanel',
  data() {
    return {
      agents: TRACEY_AGENTS,
      // Set true when Reports Marketplace promo should appear on the dashboard.
      reportsMarketplaceVisible: false,
      marketplaceUrl: 'https://reports.tendertrace.com/',
    };
  },
  methods: {
    topCapabilities(agent) {
      // Keep the panel compact: show the headline capabilities per agent.
      return (agent.capabilities || []).slice(0, agent.key === 'ultra' ? 4 : 4);
    },
    openTracey() {
      this.$router.push('/tracey').catch(() => {});
    },
  },
};
</script>

<style scoped>
.section-card {
  border: 1px solid #e5e7eb;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.whats-new__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f1f4;
}

.whats-new__header-left {
  display: flex;
  align-items: center;
}

.whats-new__title {
  font-size: 15px;
  font-weight: 700;
  color: #12253a;
}

.whats-new__badge {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #fff;
  background: #ff1463;
  padding: 2px 8px;
  border-radius: 999px;
}

.whats-new__body {
  padding: 14px 16px 16px;
}

.whats-new__intro {
  font-size: 12.5px;
  line-height: 1.5;
  color: #667085;
  margin-bottom: 12px;
}

.whats-new__agents {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.whats-new__agent {
  border: 1px solid #eceff4;
  border-left: 3px solid var(--accent);
  border-radius: 8px;
  padding: 10px 12px;
  background: #fbfcfe;
}

.whats-new__agent-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.whats-new__agent-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #12253a;
}

.whats-new__agent-tag {
  font-size: 11.5px;
  color: #98a2b3;
  margin-left: 8px;
}

.whats-new__caps {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.whats-new__cap {
  font-size: 11px;
  font-weight: 600;
  color: #344054;
  background: #eef1f6;
  border-radius: 6px;
  padding: 3px 8px;
  line-height: 1.3;
}

.whats-new__cta {
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  background: #ff1463;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.whats-new__cta:hover {
  background: #e60f58;
}

.whats-new__divider {
  height: 1px;
  background: #f0f1f4;
  margin: 14px 0;
}

.whats-new__market {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-decoration: none;
  padding: 10px;
  border: 1px solid #eceff4;
  border-radius: 8px;
  transition: border-color 0.15s, background 0.15s;
}

.whats-new__market:hover {
  border-color: #ffc2d8;
  background: #fff7fa;
}

.whats-new__market-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #f2f4f7;
}

.whats-new__market-title {
  display: flex;
  align-items: center;
  font-size: 13.5px;
  font-weight: 700;
  color: #12253a;
}

.whats-new__market-desc {
  margin: 3px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #667085;
}
</style>
